import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import useDialogA11y from './useDialog';
import { describeEntryType, formatMoney, isConversionLeg } from './expenseFormat';

/**
 * Confirm a delete before it happens.
 *
 * Deleting used to fire on the first tap, with the only safety net a toast
 * that expired in five seconds. On a phone, the Delete button sits directly
 * under Edit in a three-up row — a mis-tap cost a transaction, and if the
 * toast was missed or the page reloaded there was no way back to it.
 *
 * The dialog says exactly WHICH transaction and, for a currency conversion,
 * that two rows are going: a conversion is a clearing leg plus an FX leg, and
 * deleting either removes both. Someone deleting "the duplicate USD row" needs
 * to know the CAD row goes with it before they tap, not after.
 *
 * It also states where the entry goes, because a delete you can find again is
 * a different decision from one you cannot.
 */
const ExpenseDeleteDialog = ({ open, record, onCancel, onConfirm, busy, backgroundRef }) => {
  const dialogRef = useRef(null);
  const handleKeyDown = useDialogA11y({ open, onClose: onCancel, dialogRef, backgroundRef });

  if (!open || !record) return null;

  const pair = isConversionLeg(record);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-900/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-expense-title"
        aria-describedby="delete-expense-body"
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-t-[28px] bg-white p-5 shadow-2xl sm:rounded-[28px]"
      >
        <h2
          id="delete-expense-title"
          data-dialog-heading
          tabIndex={-1}
          className="text-lg font-semibold text-slate-900 focus:outline-none"
        >
          Delete this {describeEntryType(record).toLowerCase()}?
        </h2>

        <div id="delete-expense-body" className="mt-3 rounded-[20px] bg-slate-50 px-4 py-3 ring-1 ring-slate-200">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            <span>{record.occurred_on}</span>
            <span aria-hidden="true">•</span>
            <span>{record.category || 'Uncategorized'}</span>
          </div>
          <p className="mt-1 break-words text-base font-semibold text-slate-900">{record.description}</p>
          <p className="mt-0.5 text-base font-semibold text-slate-900">
            {formatMoney(record.currency, record.total_cost || 0)}
          </p>
        </div>

        {pair ? (
          <p className="mt-3 flex items-start gap-2 rounded-[18px] bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              This is one leg of a currency conversion. <strong>Both rows will be deleted together</strong> —
              removing only one would leave the same debt recorded twice, or erase it entirely.
            </span>
          </p>
        ) : null}

        <p className="mt-3 text-sm text-slate-500">
          You can undo this straight away, or later from <strong>Recently deleted</strong> under the ledger.
          Nothing is removed permanently.
        </p>

        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          >
            Keep it
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            aria-label={`Delete ${record.description}, ${formatMoney(record.currency, record.total_cost || 0)}`}
            className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-full border-2 border-rose-600 bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Trash2 className="h-4 w-4" aria-hidden="true" />}
            {pair ? 'Delete both rows' : 'Delete entry'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExpenseDeleteDialog;
